/**
 * UnlockContentItem
 * Flutter source: wallet_screen.dart - _unlockContentItem()
 *
 * Displays content unlock transactions.
 */
import React from 'react';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ContentPreview } from '@/presentation/feature/content/components/ContentPreview';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { MathUtils } from '@/domain/utils/mathUtils';
import { env } from '@/config/env';
import { WalletUserAvatar } from './WalletUserAvatar';

interface UnlockContentItemProps {
  transaction: AppTransactionEntity;
  currentUserId: string;
  onContentTap: (transaction: AppTransactionEntity) => void;
}

export const UnlockContentItem: React.FC<UnlockContentItemProps> = ({
  transaction,
  currentUserId,
  onContentTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();

  const isCurrentUser = transaction.usersInfo.userId === currentUserId;
  const peerName = transaction.usersInfo.peerName ?? '';

  let title: string;
  if (isCurrentUser) {
    title = `${l10n.wallet.youUnlocked} ${peerName}'s ${l10n.wallet.unlockedVideo}`;
  } else {
    const to = transaction.usersInfo.peerUserId === currentUserId ? l10n.wallet.your : `${peerName}'s`;
    title = `${transaction.usersInfo.name} ${l10n.wallet.unlocked} ${to} ${l10n.wallet.unlockedVideo}`;
  }

  const avatarUserId = isCurrentUser
    ? (transaction.usersInfo.peerUserId ?? '')
    : transaction.usersInfo.userId;

  const thumbnailUrl = transaction.mediaInfo
    ? `${env.CDN_NAME}${transaction.mediaInfo.thumbnail}`
    : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.sm }}>
      <WalletUserAvatar userId={avatarUserId} alt={transaction.usersInfo.name} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: theme.text.primary, fontSize: responsive.font.sm, fontWeight: responsive.fontWeight.medium }}>
          {title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.xs, marginTop: responsive.gap.xs }}>
          <AssetIcon
            path="/assets/images/unlocked.png"
            width={responsive.icon.small}
            height={responsive.icon.small}
            color={theme.danger}
          />
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
            - {MathUtils.roundTwoDecimals(transaction.giftInfo?.coins ?? 0)}
          </span>
          <AssetIcon
            path="/assets/images/coins.png"
            width={responsive.icon.small}
            height={responsive.icon.small}
            color={theme.exclusive}
          />
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
            · {dateFormatters.formatDate(transaction.createdDate, true)}
          </span>
        </div>
      </div>

      {thumbnailUrl && (
        <div
          style={{
            width: responsive.avatar.medium,
            height: responsive.avatar.medium,
            borderRadius: responsive.borderRadius.base,
            overflow: 'hidden',
            flexShrink: 0,
            cursor: 'pointer',
          }}
          onClick={() => onContentTap(transaction)}
        >
          <ContentPreview
            thumbnailUrl={thumbnailUrl}
            alt="Unlocked content"
          />
        </div>
      )}
    </div>
  );
};
